import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import { isNil } from '../../lib/utils'

/**
 * `InfiniteScroll` is a scrollable container which support pagination.
 *
 * Pagination is handled by 3 functions, *hasMore*, *loadMore* and *pageStart*.
 *
 * The actual querying of more elements must be done by your app. Once new content has been queried, add the data to the
 * component inside the `InfiniteScroll` and the pagination handler will resume its handling.
 *
 * @property {node} children The child components of this container.
 * @property {string} className The class name of this container. Will be appended "core-infinite-scroll-wrapper".
 * @property {node} component The type of HTML element that the component will be. Defaults to "div".
 * @property {function} hasMore A callback that should return `true` when there are more content to be loaded. *loadMore*
 * will only be called as long as *hasMore* returns `true`.
 * @property {function} loadMore A callback that triggers loading of more content. *loadMore* will only be called as long as
 * *hasMore* returns `true`.
 * @property {function} pageStart A callback which returns the next page index. `InfiniteScroll` will only call *hasMore* and
 * *loadMore* when *pageStart* returns a different page index that is not equal to the last one returned.
 * @property {number} threshold The number of pixels from the bottom of the container that has to be visible before the
 * callbacks are called. Defaults to 400px.
 * @property {boolean} windowed If set to `true` the calculation of the height will be based on the HTML document height
 * instead of the height of the infinite scroll container. Set this prop to `true` if your `InfiniteScroll` container fills
 * the entire screen. Defaults to `false`.
 * @property {number} offsetHeight Can be set to adjust for any padding, margins, borders or anything else that can affects space availability calculations.
 * This component is sensitive to css changes as it triggers loading more items based on height calculations.
 *
 * @see List, Table, Tree
 */
export default class InfiniteScroll extends Component {

  static displayName = 'InfiniteScroll';

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    component: PropTypes.string,
    hasMore: PropTypes.func,
    loadMore: PropTypes.func,
    offsetHeight: PropTypes.number,
    pageStart: PropTypes.func,
    threshold: PropTypes.number,
    windowed: PropTypes.bool
  };

  static defaultProps = {
    component: 'div',
    hasMore: () => false,
    loadMore: () => {},
    offsetHeight: 0,
    pageStart: () => 0,
    threshold: 400,
    windowed: false
  };

  componentDidMount() {
    this.addHandlers();
  }

  componentDidUpdate() {
    const { hasMore, loadMore, pageStart, offsetHeight } = this.props;
    if (hasMore() && this.page !== pageStart()) {
      const node = this.getDOMNode();
      const availableHeight = node.offsetHeight;
      let usedHeight = 0;
      for(let child of node.childNodes.values()) {
        usedHeight += child.offsetHeight;
      }
      if((availableHeight - offsetHeight) > usedHeight) {
        this.lastPage = this.page;
        this.page = pageStart()
        loadMore(this.page);
      }
    }
    this.addHandlers();
    if (!isNil(this.lastPage) && this.lastPage > this.page) {
      this.lastPage = this.page;
    }
  }

  componentWillUnmount() {
    this.removeHandlers();
  }

  onScroll = () => {
    const { hasMore, loadMore, pageStart, threshold, windowed } = this.props;
    const node = this.getDOMNode();
    const doc = document;
    let doit = false;

    this.page = pageStart();
    if (this.lastPage !== this.page) {
      if (windowed) {
        const scrollTop = window.pageYOffset || (doc.documentElement || doc.body.parentNode || doc.body).scrollTop;
        const el = this.refContainer;

        if ((this.getTopPosition(el) + el.offsetHeight) - scrollTop - window.innerHeight < Number(threshold)) {
          doit = true;
        }
      } else {
        const { clientHeight, scrollHeight } = node;

        if (node.scrollTop > 0 && node.scrollTop + clientHeight >= scrollHeight - threshold) {
          doit = true;
        }
      }
    }
    if (doit) {
      this.removeHandlers();
      this.lastPage = this.page;
      if (hasMore()) {
        loadMore(this.page);
      }
    }
  };

  getTopPosition = el => el ? el.offsetTop + this.getTopPosition(el.offsetParent) : 0; // eslint-disable-line no-confusing-arrow

  getDOMNode = () => {
    if (this.props.windowed) {
      return window;
    }
    return this.refContainer;
  };

  addHandlers = () => {
    const node = this.getDOMNode();

    if (!this.props.hasMore()) {
      return;
    }
    this.removeHandlers();
    node.addEventListener('scroll', this.onScroll);
    node.addEventListener('resize', this.onScroll);
  };

  ref = c => (this.refContainer = c);

  removeHandlers = () => {
    const node = this.getDOMNode();

    node.removeEventListener('scroll', this.onScroll);
    node.removeEventListener('resize', this.onScroll);
  };

  render() {
    const { children, className, component, hasMore, loadMore, pageStart, threshold, windowed, offsetHeight, ...props } = this.props; // eslint-disable-line no-unused-vars

    props.className = cx('core-infinite-scroll', className);
    props.ref = this.ref;
    return React.createElement(component, props, children);
  }

}
